/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { Mesh, DoubleSide, Object3D, LineBasicMaterial, Line, VertexColors, Vector3 } from 'three';
import { Director, Stage, Materials, Scene, Graph, DefaultCaps, DefaultNavigators, CapNames, SceneCompass, ColorMaps } from '@sick-davinci/glow';

import { DropDown } from '@sick-davinci/basic-elements';

export const dependencies = [DropDown];

/**
 * Demo:    Graph
 * Description:
 *   This demo application shows how to display a graph in 3D
 * Features:
 *  - Orbit navigation with custom settings
 *  - Capability handling
 *  - Graph
 *  - Scene compass
 */
export const run = ((viewParent: HTMLElement) => {

  // * Setup the stage *
  const stage = new Stage(viewParent, {
    cameras: [
      {
        name: 'default',
        from: [20, 20, 0],
        to: [0, 0, 0],
        up: [0, 0, 1],
        fov: 60,
      },
    ],
    scenes: [{
      name: 'default',
      }],
    viewports: [
      {
        name: 'default',
        scene: 'default',
        camera: 'default',
        navigator: 'orbit',
        bgcolor: 0x007fc3,
      },
    ],
  });

  // * Add an Orbit Navigator
  DefaultNavigators.initOrbit(stage, 'orbit', {rotateAround: {up: new Vector3(0, 0, 1)}});

  // * Add the scene compass for better orientation
  SceneCompass.create(stage, 'default', 'default');

  // * Data handling using capabilities
  const director = new Director(stage);
  DefaultCaps.init(director);

  director.handle(CapNames.AXIS, { axisLength: 30 });
  director.handle(CapNames.LIGHT, { payload: { lightType: 'spot', color: 0xff7777 }, orientation: { position: [10, 10, 10] } });
  director.handle(CapNames.LIGHT, { payload: { lightType: 'ambient', color: 0x999999 } });

  stage.start(); // start visualizing data

  // * GUI handling *
  const graphSel = document.getElementById('graph') as DropDown;
  graphSel.addEventListener('change', ((evt) => {
    stage.clearObject(stage.getObjectIdByName('graph'));
    drawGraph((evt as any).detail);
  }), true);
  drawGraph(graphSel.value);

  // * graph drawing *
  function drawGraph(graphName: string) {
    // * Create and add a graph to the scene
    const graph = new Graph();
    let obj: Object3D;

    switch (graphName) {

      case 'XY':
        graph.setCountX(200);
        graph.setCountY(200);
        graph.setMinX(-10);
        graph.setMaxX(10);
        graph.setMinY(-10);
        graph.setMaxY(10);

        const xyGeo = graph.createGraphXY(
          (x, y) => Math.cos(Math.sqrt(x * x + y * y) * 1.5), undefined, true,
        );

        obj = new Mesh(xyGeo, Materials.create({ type: 'standard', params: { color: 0x00ffff, side: DoubleSide } }));
        break;
        case 'ParametricT':
          graph.setColormap(ColorMaps.generate(ColorMaps.models.jet, 256, 1));
          graph.setCountU(500);
          const tGeo = graph.createParametricT(
            (t) => Math.sin(t * 4 * Math.PI) * 10 + Math.cos(t * 14 * Math.PI) * 3,
            (t) => Math.cos(t * 4 * Math.PI) * 10 + Math.sin(t * 14 * Math.PI) * 3,
            (t) => Math.sin(t * 24 * Math.PI) * 2,
            (t) => t,
          );

          const material = Materials.createLineMaterial({ params: { vertexColors: VertexColors } }) as LineBasicMaterial;
          material.needsUpdate = true;
          obj = new Line(tGeo, material);
          break;
        case 'ParametricUV':
          graph.setCountU(30);
          graph.setCountV(30);
          graph.setMinU(0);
          graph.setMaxU(2 * Math.PI);
          graph.setMinV(0);
          graph.setMaxV(2 * Math.PI);

          // for further surfaces try:
          // http://www.3d-meier.de/tut3/Seite0.html
          const uvGeo = graph.createParametricUV(
            (u, v) => 2 * Math.sin(v),
            (u, v) => (5 + 2 * Math.cos(v)) * Math.cos(u),
            (u, v) => (5 + 2 * Math.cos(v)) * Math.sin(u),
          );

          obj = new Mesh(uvGeo, Materials.create({ type: 'standard', params: { color: 0xffffff } }));
          break;
      default:
        console.warn('Graph not specified');
        break;
    }

    stage.applyThreeDirect((scene: Scene) => {
      obj.name = 'graph';
      scene.add(obj);
    });
  }
});
